import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileSpreadsheet, FileText, Loader2, Trash2, UploadCloud, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { labelCls } from "@/components/admin/BrandFormDialog";
import { CATEGORIES, errMsg, http } from "@/lib/api";
import { cn } from "@/lib/utils";

const today = () => new Date().toISOString().slice(0, 10);
const cellCls = "h-10 w-full rounded-xl border border-ph-line bg-ph-surface px-3 text-sm outline-none focus:border-ph-accent/60";
const pretty = (name) => name.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

function groupFiles(files, defaults) {
  const rows = {};
  Array.from(files).forEach((f) => {
    const dot = f.name.lastIndexOf(".");
    const ext = f.name.slice(dot + 1).toLowerCase();
    const base = f.name.slice(0, dot);
    const key = base.toLowerCase();
    const kind = ext === "pdf" ? "pdf" : ["xlsx", "xls"].includes(ext) ? "excel" : null;
    if (!kind) return;
    rows[key] = rows[key] || { key, title: pretty(base), category: defaults.category, version: "", updated_date: defaults.date, pdf: null, excel: null, status: "pending" };
    rows[key][kind] = f;
  });
  return Object.values(rows);
}

const StatusIcon = ({ row }) => {
  if (row.status === "uploading") return <Loader2 className="h-4 w-4 animate-spin text-ph-accent" />;
  if (row.status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (row.status === "error") return <XCircle className="h-4 w-4 text-red-600" title={row.error} />;
  return null;
};

export function BulkUploadDialog({ open, onOpenChange, brands, defaultBrandId, onSaved }) {
  const [brandId, setBrandId] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBrandId(defaultBrandId || ""); setCategory(""); setRows([]); setBusy(false);
  }, [open, defaultBrandId]);

  const addFiles = (files) => {
    const incoming = groupFiles(files, { category, date: today() });
    if (!incoming.length) { toast.error("Only PDF, XLSX or XLS files can be uploaded"); return; }
    setRows((prev) => {
      const map = Object.fromEntries(prev.map((r) => [r.key, r]));
      incoming.forEach((r) => { map[r.key] = map[r.key] ? { ...map[r.key], pdf: r.pdf || map[r.key].pdf, excel: r.excel || map[r.key].excel } : r; });
      return Object.values(map);
    });
  };
  const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const doneCount = rows.filter((r) => r.status === "done").length;

  const uploadAll = async () => {
    if (!brandId) { toast.error("Choose a brand first"); return; }
    const todo = rows.filter((r) => r.status !== "done");
    if (todo.some((r) => !r.category || !r.title.trim() || !r.updated_date)) { toast.error("Every row needs a name, category and date"); return; }
    setBusy(true);
    let ok = 0;
    for (const r of todo) {
      patch(r.key, { status: "uploading", error: "" });
      const fd = new FormData();
      fd.append("brand_id", brandId);
      ["title", "category", "version", "updated_date"].forEach((k) => fd.append(k, r[k]));
      if (r.pdf) fd.append("pdf", r.pdf);
      if (r.excel) fd.append("excel", r.excel);
      try {
        const { data } = await http.post("/admin/documents", fd);
        const dups = Array.isArray(data?.duplicates) ? data.duplicates : [];
        patch(r.key, { status: "done", error: dups.length ? `Saved as draft. Possible duplicate of: ${dups.join("; ")}` : "" });
        ok += 1;
      } catch (err) {
        patch(r.key, { status: "error", error: errMsg(err) });
      }
    }
    setBusy(false);
    onSaved();
    if (ok === todo.length) { toast.success(`${ok} price lists saved as drafts`, { description: "Publish them from the Price Lists tab when ready." }); onOpenChange(false); }
    else toast.error(`${todo.length - ok} of ${todo.length} uploads failed — see the red rows`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-3xl" data-testid="bulk-upload-dialog" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-ph-ink">Bulk upload price lists</DialogTitle>
          <DialogDescription>Drop many files at once. A PDF and an Excel file with the same name become one price list.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className={labelCls}>Brand</p>
            <Select value={brandId || undefined} onValueChange={setBrandId} disabled={busy}>
              <SelectTrigger className="mt-1.5 h-12 rounded-2xl" data-testid="bulk-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <p className={labelCls}>Default category for new rows</p>
            <Select value={category || undefined} onValueChange={(v) => { setCategory(v); setRows((rs) => rs.map((r) => (r.category ? r : { ...r, category: v }))); }} disabled={busy}>
              <SelectTrigger className="mt-1.5 h-12 rounded-2xl" data-testid="bulk-default-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="bulk-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          className={cn("flex w-full flex-col items-center rounded-3xl border-2 border-dashed px-6 py-10 transition-colors",
            drag ? "border-ph-accent bg-ph-violet/10" : "border-ph-line hover:border-ph-accent/50 hover:bg-ph-raised")}>
          <UploadCloud className="h-9 w-9 text-ph-accent" />
          <p className="mt-3 font-display text-lg font-bold text-ph-ink">Drop PDF / Excel files here</p>
          <p className="text-sm text-ph-muted">or click to choose · max 25 MB each</p>
        </button>
        <input ref={inputRef} type="file" multiple accept=".pdf,.xlsx,.xls" className="hidden" data-testid="bulk-file-input"
          onChange={(e) => { addFiles(e.target.files || []); e.target.value = ""; }} />

        {rows.length > 0 && (
          <div className="space-y-2" data-testid="bulk-rows">
            {rows.map((r) => (
              <div key={r.key} data-testid={`bulk-row-${r.key}`}
                className={cn("grid grid-cols-1 items-center gap-2 rounded-2xl border p-3 md:grid-cols-[1.6fr_1.3fr_0.7fr_0.9fr_auto]",
                  r.status === "error" ? "border-red-200 bg-red-50/60" : r.status === "done" ? "border-emerald-200 bg-emerald-50/50" : "border-ph-line")}>
                <div>
                  <input value={r.title} onChange={(e) => patch(r.key, { title: e.target.value })} disabled={busy || r.status === "done"} className={cellCls} aria-label="Price list name" data-testid={`bulk-title-${r.key}`} />
                  <div className="mt-1.5 flex gap-1.5 px-1 text-[11px]">
                    {r.pdf && <span className="inline-flex items-center gap-1 rounded-full bg-ph-raised px-2 py-0.5 text-ph-ink/85"><FileText className="h-3 w-3" />PDF</span>}
                    {r.excel && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800"><FileSpreadsheet className="h-3 w-3" />Excel</span>}
                    {r.error && <span className="text-red-700">{r.error}</span>}
                  </div>
                </div>
                <select value={r.category} onChange={(e) => patch(r.key, { category: e.target.value })} disabled={busy || r.status === "done"} className={cellCls} aria-label="Category" data-testid={`bulk-category-${r.key}`}>
                  <option value="">Category…</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={r.version} onChange={(e) => patch(r.key, { version: e.target.value })} placeholder="Revision" disabled={busy || r.status === "done"} className={cellCls} aria-label="Revision" data-testid={`bulk-version-${r.key}`} />
                <input type="date" value={r.updated_date} onChange={(e) => patch(r.key, { updated_date: e.target.value })} disabled={busy || r.status === "done"} className={cellCls} aria-label="Updated date" data-testid={`bulk-date-${r.key}`} />
                <div className="flex items-center justify-end gap-1">
                  <StatusIcon row={r} />
                  {!busy && r.status !== "done" && (
                    <button type="button" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} aria-label="Remove row" data-testid={`bulk-remove-${r.key}`} className="rounded-full p-2 text-ph-muted hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {busy && <Progress value={(doneCount / Math.max(rows.length, 1)) * 100} className="h-2" />}
        <button type="button" onClick={uploadAll} disabled={busy || rows.length === 0} data-testid="bulk-submit"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ph-lime font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)] disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? `Uploading ${doneCount + 1} of ${rows.length}…` : `Publish ${rows.filter((r) => r.status !== "done").length || ""} price lists`}
        </button>
      </DialogContent>
    </Dialog>
  );
}
